import React, {Suspense, useMemo, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {Autoplay, EffectFade} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {diApiNa} from '@di-shared/api/di';
import {UNIT} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import useErrorCounter from '@di-shared/features/common/useErrorCounter';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {EvBatteryPerformanceStatus} from '@di-shared/features/home/kr/ev-battery/monitor/const';
import {getIsLowBatteryChargeBannerVisible, getIsMonthlyChargeRemindeBannerVisible} from '@di-shared/features/home/kr/ev-battery/util';
import useOpenChargingHabitBottomSheetNa from '@di-shared/features/home/na/ev-battery/useOpenChargingHabitBottomSheet.na';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface HomeEvBatteryNaProps {
  vin: string;
}

function HomeEvBatteryNa({vin}: HomeEvBatteryNaProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<Loading />}>
        <HomeEvBatteryNaInner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

// ============================================================================
// Inner Component with Business Logic
// ============================================================================

function HomeEvBatteryNaInner({vin}: HomeEvBatteryNaProps) {
  const {t} = useInferableTranslation();
  return (
    <section className="section section_ev_battery">
      <div className="section_container">
        <div className="section_container_header">
          <h3 className="title_xxsmall">{t('ctn_evb_title_eu')}</h3>
        </div>

        <BatteryGuideMessage />

        <ChargingEventNotification vin={vin} />

        <div className="battery_status_area type_vertical">
          <EvBatteryPerformance vin={vin} />
          <EvBatteryChargingHabit vin={vin} />
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Battery Guide Message Component
// ============================================================================

function BatteryGuideMessage() {
  const {t} = useInferableTranslation();
  const BATTERY_GUIDE_MESSAGES = useMemo(
    () => [
      t('msg_evb_mng_guide_keep_20_80_reduce_degradation_eu'),
      t('msg_ebv_mng_guide_avoid_full_discharge_eu'),
      t('msg_ebv_mng_guide_smart_bms_monitor_detect_eu'),
      t('msg_ebv_mng_guide_nav_set_fast_charge_enable_preconditioning_eu'),
      t('msg_ebv_mng_guide_scheduled_climate_control_minimize_drain_eu'),
    ],
    []
  );
  const [message] = useState(BATTERY_GUIDE_MESSAGES[Math.floor(Math.random() * BATTERY_GUIDE_MESSAGES.length)]);
  return <p className="battery_guide_text">{message}</p>;
}

// ============================================================================
// Loading Component
// ============================================================================

function Loading() {
  return (
    <section className="section section_ev_battery">
      <div
        className="section_container skeleton"
        style={{height: 330}}
      />
    </section>
  );
}

// ============================================================================
// EvBatteryPerformance Component
// ============================================================================

function getEvBatteryPerformanceStatus(soh: number) {
  /**
   * @link https://jira.hmg-corp.io/browse/DRIVINGINS-968
   * soh가 0일 경우 에러 처리
   */
  if (soh <= 0) {
    return EvBatteryPerformanceStatus.ERROR;
  }
  if (80 <= soh) {
    return EvBatteryPerformanceStatus.GOOD;
  }
  return EvBatteryPerformanceStatus.NORMAL;
}

interface EvBatteryPerformanceStatusProps {
  vin: string;
}

function EvBatteryPerformance(props: EvBatteryPerformanceStatusProps) {
  const {t} = useInferableTranslation();
  return (
    <ErrorBoundary fallback={<NoData title={t('ctn_evb_performance_status_desc_eu')} />}>
      <EvBatteryPerformanceInner {...props} />
    </ErrorBoundary>
  );
}

function EvBatteryPerformanceInner({vin}: EvBatteryPerformanceStatusProps) {
  const {t} = useInferableTranslation();
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.evBattery.getEvBatteryStatus), vin],
    queryFn: () => diApiNa.evBattery.getEvBatteryStatus({vin}),
  });

  /**
   * [2026-03-10:Jeyoung Lee]
   * Nodata, api error, SoH = 0 일때 analyzing case
   */
  if (data.soh === 0) {
    throw new ExpectedError('soh is 0.');
  }

  const evBatteryPerformanceStatus = getEvBatteryPerformanceStatus(data.soh);

  switch (evBatteryPerformanceStatus) {
    case EvBatteryPerformanceStatus.GOOD:
      return <EvBatteryPerformanceGood />;
    case EvBatteryPerformanceStatus.NORMAL:
      return <EvBatteryPerformanceNormal />;
    case EvBatteryPerformanceStatus.ERROR:
    default:
      return <NoData title={t('ctn_evb_performance_status_desc_eu')} />;
  }
}

function EvBatteryPerformanceGood() {
  const {t} = useInferableTranslation();
  return (
    <div className="battery_status_box">
      <a
        role="button"
        className="battery_status_link"
        onClick={async () => {
          emitEventLog({
            event: 'vehicle_status_check',
            interaction_type: 'ev_soh_status',
          });
          await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_PERFORMANCE.getFullPath(), {
            evBatteryPerformanceStatus: EvBatteryPerformanceStatus.GOOD,
          });
        }}
      >
        <div className="status-inner">
          <span className="ic ic_filled_good_face"></span>
          <div className="battery_info">
            <p className="battery_status">{t('ctn_evb_performance_status_good_eu')}</p>
          </div>
        </div>
        <span className="battery_title">{t('ctn_evb_performance_status_desc_eu')}</span>
      </a>
    </div>
  );
}

function EvBatteryPerformanceNormal() {
  const {t} = useInferableTranslation();
  return (
    <div className="battery_status_box">
      <a
        role="button"
        className="battery_status_link"
        onClick={async () => {
          emitEventLog({
            event: 'vehicle_status_check',
            interaction_type: 'ev_soh_status',
          });
          await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_BATTERY_PERFORMANCE.getFullPath(), {
            evBatteryPerformanceStatus: EvBatteryPerformanceStatus.NORMAL,
          });
        }}
      >
        <div className="status-inner">
          <span className="ic ic_filled_normal_face"></span>
          <div className="battery_info">
            <p className="battery_status">{t('ctn_evb_performance_status_normal_eu')}</p>
          </div>
        </div>
        <span className="battery_title">{t('ctn_evb_performance_status_desc_eu')}</span>
      </a>
    </div>
  );
}

// ============================================================================
// ChargingEventNotification Component
// ============================================================================

interface EvBatteryMonitorBannerProps {
  vin: string;
}

function ChargingEventNotification(props: EvBatteryMonitorBannerProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<></>}>
        <ChargingEventNotificationInner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function ChargingEventNotificationInner({vin}: EvBatteryMonitorBannerProps) {
  const {t} = useInferableTranslation();

  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.evBattery.getEvBatteryStatus), vin],
    queryFn: () => diApiNa.evBattery.getEvBatteryStatus({vin}),
  });

  const isLowBatteryChargeBannerVisible = getIsLowBatteryChargeBannerVisible(data.soc);
  const isMonthlyChargeRemindeBannerVisible = getIsMonthlyChargeRemindeBannerVisible(parseDateWithFormat(data.lastChargedDate));
  if (isLowBatteryChargeBannerVisible) {
    return (
      <div className="guide_banner battery_guide">
        <span className="ic ic_filled_caution_16px" />
        <p
          className="guide_banner_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_evb_evt_alert_guide_charge_recommend_before_10_eu')}
        </p>
      </div>
    );
  }
  if (isMonthlyChargeRemindeBannerVisible) {
    return (
      <div className="guide_banner battery_guide">
        <span className="ic ic_filled_caution_16px" />
        <p
          className="guide_banner_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_evb_evt_alert_guide_month_since_last_charge_recommend_charge_eu')}
        </p>
      </div>
    );
  }
  return <></>;
}

// ============================================================================
// EvBatteryChargingHabit Component
// ============================================================================

interface EVBatteryPerformanceStatusProps {
  vin: string;
}

function EvBatteryChargingHabit(props: EVBatteryPerformanceStatusProps) {
  const {t} = useInferableTranslation();
  return (
    <ErrorBoundary fallback={<NoData title={t('ctn_evb_charging_habit_desc_eu')} />}>
      <EvBatteryChargingHabitInner {...props} />
    </ErrorBoundary>
  );
}

const NUMBER_OF_COMPONENT = 2;
function EvBatteryChargingHabitInner({vin}: EVBatteryPerformanceStatusProps) {
  const {data: evBatteryStatusData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.evBattery.getEvBatteryStatus), vin],
    queryFn: () => diApiNa.evBattery.getEvBatteryStatus({vin}),
  });

  /**
   * [2026-03-10:Jeyoung Lee]
   * Nodata, api error, SoH = 0 일때 analyzing case
   */
  if (evBatteryStatusData.data.soh === 0) {
    throw new ExpectedError('soh is 0.');
  }

  const {isError, emitError, isErrorAtIndex} = useErrorCounter(NUMBER_OF_COMPONENT);
  if (isError) {
    throw new ExpectedError();
  }

  return (
    <>
      <Swiper
        className="battery-status-box-swiper"
        effect={'fade'}
        slidesPerView={'auto'}
        modules={[Autoplay, EffectFade]}
        autoplay={{delay: 4000, disableOnInteraction: false}}
        observer
        observeParents
        allowTouchMove={false}
      >
        {!isErrorAtIndex(0) && (
          <SwiperSlide className={'battery_status_box'}>
            <ErrorBoundary
              fallback={<></>}
              onError={() => emitError(0)}
            >
              <ChargeRangeStats vin={vin} />
            </ErrorBoundary>
          </SwiperSlide>
        )}
        {!isErrorAtIndex(1) && (
          <SwiperSlide className={'battery_status_box'}>
            <ErrorBoundary
              fallback={<></>}
              onError={() => emitError(1)}
            >
              <LowBatteryAlertCounter vin={vin} />
            </ErrorBoundary>
          </SwiperSlide>
        )}
      </Swiper>
    </>
  );
}

interface ChargeRangeStatsProps {
  vin: string;
}

function ChargeRangeStats({vin}: ChargeRangeStatsProps) {
  const {t} = useInferableTranslation();
  const {openChargingHabitBottomSheetNa} = useOpenChargingHabitBottomSheetNa({vin});
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.evBattery.getEvChargeSummary), vin],
    queryFn: () => diApiNa.evBattery.getEvChargeSummary({vin}),
  });

  return (
    <a
      className="battery_status_link"
      role={'button'}
      onClick={openChargingHabitBottomSheetNa}
    >
      <div className="status-inner">
        <span className="ic ic_filled_ev_charging"></span>
        <div className="battery_info">
          <p className="battery_status">{`${Math.round(data.avgMinSoc)}-${Math.round(data.avgMaxSoc)}${UNIT.PERCENT}`}</p>
          <p className="battery_desc">{t('ctn_evb_charging_habits_average_charging_range_eu')}</p>
        </div>
      </div>
      <span className="battery_title">{t('ctn_evb_charging_habit_desc_eu')}</span>
    </a>
  );
}

interface LowBatteryAlertCounterProps {
  vin: string;
}

function LowBatteryAlertCounter({vin}: LowBatteryAlertCounterProps) {
  const {t} = useInferableTranslation();
  const {openChargingHabitBottomSheetNa} = useOpenChargingHabitBottomSheetNa({vin});

  const {data: evChargeHistoryData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiNa.evBattery.getEvChargeHistory), vin],
    queryFn: () => diApiNa.evBattery.getEvChargeHistory({vin}),
  });

  const LowBatteryCount = evChargeHistoryData.data.chargedDays.reduce((acc, cur) => acc + cur.lowSocAlert, 0);

  if (LowBatteryCount <= 0) {
    throw new ExpectedError();
    /**
     * @link https://jira.hmg-corp.io/browse/ICTQMSCHE-14776
     * 0회일 경우 의도적으로 에러를 반환하게 하여 스와이퍼에서 LowBatteryAlertCounter를 뺌
     */
  }

  return (
    <a
      className="battery_status_link"
      role="button"
      onClick={openChargingHabitBottomSheetNa}
    >
      <div className="status-inner">
        <span className="ic ic_filled_caution"></span>
        <div className="battery_info">
          <p className="battery_status">{t('ctn_evb_charging_habits_count_eu', {count: LowBatteryCount})}</p>
          <p className="battery_desc">{t('ctn_evb_charging_habits_below_10_eu')}</p>
        </div>
      </div>
      <span className="battery_title">{t('ctn_evb_charging_habit_desc_eu')}</span>
    </a>
  );
}

function NoData({title}: {title: string}): JSX.Element {
  const {t} = useInferableTranslation();
  return (
    <div className="battery_status_box">
      <a className="battery_status_link">
        <div className="status-inner">
          <div className="loading_lottie">
            <LottieSelector
              id="img_json_loading_black_60f"
              kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              loop={true}
              autoplay={true}
              style={{width: 24, height: 24}}
            />
          </div>
          <div className="battery_info">
            <p className="battery_status">{t('ctn_evb_charging_habits_analyzing_eu')}</p>
          </div>
        </div>
        <span className="battery_title">{title}</span>
      </a>
    </div>
  );
}

export default HomeEvBatteryNa;
